import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getOpenShift, getUnclosedDates, closeShift, getShiftClosings,
} from '../../redux/features/sales/shiftClosingSlice';
import { useRedirectLogOutUser } from "../../customHook/useRedirectLogOutUser";
import { useRedirectEmployee } from "../../customHook/useRedirectEmploye";
import { formatEthiopianDate } from '../../utils/ethiopianCalendar';
import ButtonLoading from '../../loader/ButtonLoader';
import { toast } from 'react-toastify';
import { Modal } from "bootstrap";
import { useTable, usePagination, useGlobalFilter, useFilters } from 'react-table';
import { GlobalFilter } from '../globalFilter';
import { SelectColumnFilter, multiSelectFilter } from './dashBoard/SelectColumnFilter1';
import { ColumnToggle } from './ColumnToggle';

const fmt  = (n) => Number(n || 0).toLocaleString();
const dash = (v) => (v === null || v === undefined || v === "") ? "—" : v;

export const ShiftClosing = () => {
  useRedirectLogOutUser();
  useRedirectEmployee();

  const dispatch = useDispatch();
  const confirmModalRef = useRef(null);

  const { openShift, unclosedDates, closings, isLoading } =
    useSelector(s => s.shiftClosing);

    console.log("openShift", openShift, "unclosedDates", unclosedDates, "closings", closings, "isLoading", isLoading);

  const [countedCash, setCountedCash] = useState("");
  const [countedBank, setCountedBank] = useState("");
  const [note, setNote] = useState("");
  const [showHistory, setShowHistory] = useState(false);
  const [showSalesList, setShowSalesList] = useState(true);

  const hasFetched = useRef(false);
  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    dispatch(getOpenShift());
    dispatch(getUnclosedDates());
    dispatch(getShiftClosings());
  }, [dispatch]);

  // ── Variance calculations — live ──────────────────────────
  const cashVariance = useMemo(() => {
    if (!openShift || countedCash === "") return null;
    return +(Number(countedCash) - openShift.expectedCash).toFixed(2);
  }, [countedCash, openShift]);

  const bankVariance = useMemo(() => {
    if (!openShift || countedBank === "") return null;
    return +(Number(countedBank) - openShift.expectedBank).toFixed(2);
  }, [countedBank, openShift]);

  const hasVariance = (cashVariance !== null && cashVariance !== 0) ||
                       (bankVariance !== null && bankVariance !== 0);

  // ── Normalize sales for filterable columns ──────────────────
  const salesData = useMemo(() => {
    if (!openShift?.sales) return [];
    return openShift.sales.map(sale => ({
      ...sale,
      _customerName: sale.customer?.name || sale.customerName || "Walk-in",
      _productNames: sale.items?.map(i => i.product?.name || "—").join(", ") || "",
    }));
  }, [openShift?.sales]);

  // ── Sales table columns ────────────────────────────────────
  const salesColumns = useMemo(() => [
    { Header: 'No', id: 'no', Cell: ({ row }) => row.index + 1 },
    {
      Header: 'Time',
      accessor: 'createdAt',
      Cell: ({ value }) => (
        <span className="text-muted">
          {new Date(value).toLocaleTimeString([], {
            hour: '2-digit', minute: '2-digit'
          })}
        </span>
      )
    },
    {
      Header: 'Items',
      accessor: '_productNames',
      Filter: SelectColumnFilter,
      filter: 'multiSelectFilter',
      Cell: ({ row }) => (
        <div className="d-flex flex-wrap gap-1">
          {row.original.items?.slice(0, 2).map((item, ii) => (
            <span key={ii}
              className="badge bg-primary bg-opacity-10 text-primary
                         rounded-pill px-2 py-1 fs-5">
              {item.product?.name || "—"} × {item.quantity}
            </span>
          ))}
          {row.original.items?.length > 2 && (
            <span className="badge bg-secondary bg-opacity-10
                             text-secondary rounded-pill px-2 py-1 fs-5">
              +{row.original.items.length - 2} more
            </span>
          )}
        </div>
      )
    },
    {
      Header: 'Customer',
      accessor: '_customerName',
      Filter: SelectColumnFilter,
      filter: 'multiSelectFilter',
      Cell: ({ value }) => value
    },
    {
      Header: 'Payment',
      accessor: 'paymentMethod',
      Filter: SelectColumnFilter,
      filter: 'multiSelectFilter',
      Cell: ({ value, row }) => (
        <div>
          <span className="text-capitalize">{value}</span>
          {value === "bank" && row.original.bankName && (
            <span className="badge bg-info bg-opacity-25 text-info
                             ms-1 rounded-pill px-2 py-1 fs-6">
              {row.original.bankName}
            </span>
          )}
          {value === "split" && row.original.splitPayments?.length > 0 && (
            <div className="mt-1">
              {row.original.splitPayments.map((sp, si) => (
                <span key={si}
                  className="badge bg-secondary bg-opacity-15
                             text-secondary rounded-pill px-2 py-1 fs-6 me-1">
                  {sp.method}: {fmt(sp.amount)}
                </span>
              ))}
            </div>
          )}
        </div>
      )
    },
    {
      Header: 'Total',
      accessor: 'totalAmount',
      Cell: ({ value }) => <span className="fw-semibold">{fmt(value)}</span>
    },
    {
      Header: 'Paid',
      accessor: 'paidAmount',
      Cell: ({ value }) => <span className="text-success">{fmt(value)}</span>
    },
    {
      Header: 'Unpaid',
      accessor: 'unpaidAmount',
      Cell: ({ value }) => value > 0
        ? <span className="text-danger">{fmt(value)}</span>
        : <span className="text-muted">—</span>
    },
  ], []);

  const salesFilterTypes = useMemo(() => ({ multiSelectFilter }), []);

  const salesTable = useTable({
    columns: salesColumns,
    data: salesData,
    filterTypes: salesFilterTypes,
    defaultColumn: { Filter: () => null },
    initialState: { pageSize: 10 },
  }, useFilters, useGlobalFilter, usePagination);

  const {
    getTableProps: getSalesTableProps,
    getTableBodyProps: getSalesTableBodyProps,
    headerGroups: salesHeaderGroups,
    page: salesPage,
    nextPage: salesNextPage,
    previousPage: salesPreviousPage,
    canNextPage: salesCanNextPage,
    canPreviousPage: salesCanPreviousPage,
    gotoPage: salesGotoPage,
    pageCount: salesPageCount,
    setGlobalFilter: setSalesGlobalFilter,
    pageOptions: salesPageOptions,
    setPageSize: setSalesPageSize,
    state: salesState,
    prepareRow: prepareSalesRow,
    allColumns: salesAllColumns,
    getToggleHideAllColumnsProps: getSalesToggleHideAllColumnsProps,
  } = salesTable;

  const {
    pageIndex: salesPageIndex,
    globalFilter: salesGlobalFilter,
    pageSize: salesPageSize,
  } = salesState;

  // ── Submit ─────────────────────────────────────────────────
  const openConfirmModal = () => {
    if (countedCash === "" || countedBank === "") {
      return toast.error("Please enter counted cash and bank amounts");
    }
    document.activeElement?.blur();
    new Modal(confirmModalRef.current).show();
  };

  const handleConfirmClose = async () => {
    const result = await dispatch(closeShift({
      countedCash: Number(countedCash),
      countedBank: Number(countedBank),
      note: note || (hasVariance
        ? "Variance noted at closing"
        : null),
    }));

    if (!result.error) {
      setCountedCash("");
      setCountedBank("");
      setNote("");
      dispatch(getOpenShift());
      dispatch(getUnclosedDates());
      dispatch(getShiftClosings());
      Modal.getInstance(confirmModalRef.current)?.hide();
    }
  };

  if (!openShift) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" />
      </div>
    );
  }

  return (
    <div className="px-4 py-3">

      {/* ── CONFIRM MODAL ──────────────────────────────────── */}
      <div className="modal fade" ref={confirmModalRef} tabIndex="-1">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content border-0 rounded-4 shadow-lg p-3">
            <div className="modal-header border-0 pb-0">
              <h5 className="modal-title fw-semibold fs-3">
                {hasVariance ? "⚠️ Close Shift With Variance?" : "Close Shift?"}
              </h5>
              <button className="btn-close ms-auto" data-bs-dismiss="modal" />
            </div>
            <div className="modal-body py-3">
              <div className="bg-light rounded-3 px-3 py-3">
                <div className="d-flex justify-content-between mb-2 fs-4">
                  <span>Cash variance</span>
                  <span className={`fw-bold ${
                    cashVariance === 0 ? "text-success"
                    : cashVariance > 0 ? "text-primary" : "text-danger"
                  }`}>
                    {cashVariance > 0 ? "+" : ""}{fmt(cashVariance)} birr
                  </span>
                </div>
                <div className="d-flex justify-content-between fs-4">
                  <span>Bank variance</span>
                  <span className={`fw-bold ${
                    bankVariance === 0 ? "text-success"
                    : bankVariance > 0 ? "text-primary" : "text-danger"
                  }`}>
                    {bankVariance > 0 ? "+" : ""}{fmt(bankVariance)} birr
                  </span>
                </div>
                {hasVariance && (
                  <p className="text-muted fs-5 mt-2 mb-0">
                    This will be permanently recorded. Once closed,
                    this shift cannot be reopened.
                  </p>
                )}
              </div>
            </div>
            <div className="modal-footer border-0 pt-0 gap-2">
              <button className="btn btn-primary border flex-fill rounded-3 fs-4"
                data-bs-dismiss="modal">Cancel</button>
              {isLoading ? (
                <ButtonLoading className="btn btn-success flex-fill rounded-3 fs-4" disabled>
                  Closing...
                </ButtonLoading>
              ) : (
                <button className="btn btn-success flex-fill rounded-3 fs-4"
                  onClick={handleConfirmClose}>
                  Confirm Close
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── HEADER ──────────────────────────────────────────── */}
      <div className="d-flex justify-content-between align-items-center
                      mb-5 px-4 py-3 rounded-4 border"
        style={{ backgroundColor: "#faf8f5" }}>
        <div>
          <h4 className="mb-0 fs-2">Shift Closing</h4>
          <small className="text-muted fs-5">
            Open since {formatEthiopianDate(openShift.shiftStart, { includeTime: true })}
          </small>
        </div>
        <button
          className={`btn rounded-3 fs-4 ${showHistory ? "btn-primary" : "btn-outline-primary"}`}
          onClick={() => setShowHistory(prev => !prev)}>
          {showHistory ? "← Back to Open Shift" : "History"}
        </button>
      </div>

      {showHistory ? (
        /* ── HISTORY VIEW ──────────────────────────────────── */
        <div className="table-responsive rounded-4 border" style={{ backgroundColor: "#fff" }}>
          <table className="table table-hover mb-0 fs-4">
            <thead>
              <tr style={{ backgroundColor: "#f0fff4" }}>
                <th className="px-4 py-3">Period</th>
                <th className="px-4 py-3 text-end">Expected Cash</th>
                <th className="px-4 py-3 text-end">Counted Cash</th>
                <th className="px-4 py-3 text-end">Cash Variance</th>
                <th className="px-4 py-3 text-end">Expected Bank</th>
                <th className="px-4 py-3 text-end">Counted Bank</th>
                <th className="px-4 py-3 text-end">Bank Variance</th>
                <th className="px-4 py-3">Closed By</th>
              </tr>
            </thead>
            <tbody>
              {closings.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center text-muted py-5">
                    No shifts closed yet.
                  </td>
                </tr>
              ) : closings.map(c => (
                <tr key={c._id}>
                  <td className="px-4 py-3">
                    {formatEthiopianDate(c.shiftStart, { includeTime: true })}
                    {" → "}
                    {formatEthiopianDate(c.shiftEnd, { includeTime: true })}
                  </td>
                  <td className="px-4 py-3 text-end">{fmt(c.expectedCash)}</td>
                  <td className="px-4 py-3 text-end">{fmt(c.countedCash)}</td>
                  <td className={`px-4 py-3 text-end fw-semibold ${
                    c.cashVariance === 0 ? "text-success"
                    : c.cashVariance > 0 ? "text-primary" : "text-danger"
                  }`}>
                    {c.cashVariance > 0 ? "+" : ""}{fmt(c.cashVariance)}
                  </td>
                  <td className="px-4 py-3 text-end">{fmt(c.expectedBank)}</td>
                  <td className="px-4 py-3 text-end">{fmt(c.countedBank)}</td>
                  <td className={`px-4 py-3 text-end fw-semibold ${
                    c.bankVariance === 0 ? "text-success"
                    : c.bankVariance > 0 ? "text-primary" : "text-danger"
                  }`}>
                    {c.bankVariance > 0 ? "+" : ""}{fmt(c.bankVariance)}
                  </td>
                  <td className="px-4 py-3">{c.closedBy?.firstname || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        /* ── OPEN SHIFT VIEW ───────────────────────────────── */
        <>
          {unclosedDates.length > 1 && (
            <div className="alert alert-warning border-0 rounded-3 fs-4 mb-5">
              ⚠️ This open period spans {unclosedDates.length} days:{" "}
              {unclosedDates.map(d =>
                formatEthiopianDate(new Date(d + "T12:00:00"))
              ).join(", ")}
              . All sales in this range will be included when you close.
            </div>
          )}

          {/* ── Summary cards ───────────────────────────────── */}
          <div className="row g-4 mb-5">
            <div className="col-12 col-md-4">
              <div className="card border-0 shadow-sm rounded-4 h-100">
                <div className="card-body p-4 text-center">
                  <p className="text-muted mb-1 fs-4">💵 Expected Cash</p>
                  <p className="fw-bold fs-2 mb-0 text-success">
                    {fmt(openShift.expectedCash)} birr
                  </p>
                </div>
              </div>
            </div>
            <div className="col-12 col-md-4">
              <div className="card border-0 shadow-sm rounded-4 h-100">
                <div className="card-body p-4 text-center">
                  <p className="text-muted mb-1 fs-4">🏦 Expected Bank</p>
                  <p className="fw-bold fs-2 mb-0 text-primary">
                    {fmt(openShift.expectedBank)} birr
                  </p>
                  {openShift.expectedBankBreakdown?.length > 0 && (
                    <div className="mt-2">
                      {openShift.expectedBankBreakdown.map((b, i) => (
                        <div key={i} className="d-flex justify-content-between fs-5 text-muted">
                          <span>{b.bankName}</span>
                          <span>{fmt(b.amount)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="col-12 col-md-4">
              <div className="card border-0 shadow-sm rounded-4 h-100">
                <div className="card-body p-4 text-center">
                  <p className="text-muted mb-1 fs-4">📋 Expected Credit</p>
                  <p className="fw-bold fs-2 mb-0 text-warning">
                    {fmt(openShift.expectedCredit)} birr
                  </p>
                  <small className="text-muted">Not physical cash — informational</small>
                </div>
              </div>
            </div>
          </div>

          {/* ── SALES LIST — react-table with pagination/filters ── */}
          <div className="card border-0 shadow-sm rounded-4 mb-5">
            <div className="card-header bg-white border-bottom rounded-top-4
                            px-4 py-3 d-flex justify-content-between align-items-center"
              style={{ cursor: "pointer" }}
              onClick={() => setShowSalesList(prev => !prev)}>
              <h5 className="fw-semibold mb-0 fs-3">
                Transactions in This Shift
                <span className="badge bg-primary bg-opacity-25 text-primary
                                 rounded-pill ms-2 px-3 py-1 fs-5">
                  {openShift.sales?.length || 0} sales
                </span>
                {openShift.externalPayments?.length > 0 && (
                  <span className="badge bg-warning bg-opacity-25 text-warning
                                   rounded-pill ms-2 px-3 py-1 fs-5">
                    {openShift.externalPayments.length} payments
                  </span>
                )}
              </h5>
              <span className="fs-3 text-muted">
                {showSalesList ? "▲" : "▼"}
              </span>
            </div>

            {showSalesList && (
              <div className="card-body p-0">

                {openShift.sales?.length > 0 ? (
                  <>
                    {/* Search + column toggle */}
                    <div className="px-4 pt-3 pb-2">
                      <div className="d-flex align-items-center justify-content-between
                                      gap-3 px-3 rounded-3 border"
                        style={{ backgroundColor: "#faf8f5" }}>
                        <GlobalFilter filter={salesGlobalFilter} setFilter={setSalesGlobalFilter} />
                        <ColumnToggle
                          allColumns={salesAllColumns}
                          getToggleHideAllColumnsProps={getSalesToggleHideAllColumnsProps}
                        />
                      </div>
                    </div>

                    {/* Table */}
                    <div className="table-responsive">
                      <table className="table table-hover mb-0 fs-4" {...getSalesTableProps()}>
                        <thead>
                          {salesHeaderGroups.map(hg => (
                            <tr {...hg.getHeaderGroupProps()}>
                              {hg.headers.map(col => (
                                <th className="px-4 py-3"
                                  style={{ backgroundColor: "#f0fff4" }}
                                  {...col.getHeaderProps()}>
                                  {col.render('Header')}
                                  {col.canFilter && col.render('Filter')}
                                </th>
                              ))}
                            </tr>
                          ))}
                        </thead>
                        <tbody {...getSalesTableBodyProps()}>
                          {salesPage.map(row => {
                            prepareSalesRow(row);
                            return (
                              <tr {...row.getRowProps()}>
                                {row.cells.map(cell => (
                                  <td className="px-4 py-3" {...cell.getCellProps()}>
                                    {cell.render('Cell')}
                                  </td>
                                ))}
                              </tr>
                            );
                          })}
                        </tbody>
                        <tfoot>
                          <tr className="table-success fw-bold">
                            <td colSpan={5} className="px-4 py-3">TOTAL (all sales)</td>
                            <td className="px-4 py-3">
                              {fmt(openShift.sales.reduce((s, x) => s + (x.totalAmount || 0), 0))}
                            </td>
                            <td className="px-4 py-3">
                              {fmt(openShift.sales.reduce((s, x) => s + (x.paidAmount || 0), 0))}
                            </td>
                            <td className="px-4 py-3 text-danger">
                              {fmt(openShift.sales.reduce((s, x) => s + (x.unpaidAmount || 0), 0))}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>

                    {/* Pagination */}
                    <div className="px-4 py-3">
                      <ul className="pagination justify-content-center pagination-lg mb-0">
                        <span style={{ padding: "5px", fontSize: '14px' }}>
                          page <strong>{salesPageIndex + 1}</strong> of {salesPageOptions.length}
                        </span>
                        <select className="form-select form-select-lg" style={{ maxWidth: '90px' }}
                          value={salesPageSize}
                          onChange={e => setSalesPageSize(Number(e.target.value))}>
                          {[10, 20, 30, 50].map((s, i) => (
                            <option key={i} value={s}>Show {s}</option>
                          ))}
                        </select>
                        <li><button className="page-link" onClick={() => salesGotoPage(0)}
                          disabled={!salesCanPreviousPage}>{'<<'}</button></li>
                        <li className={`page-item ${!salesCanPreviousPage ? 'disabled' : ''}`}>
                          <button className="page-link" onClick={salesPreviousPage}
                            disabled={!salesCanPreviousPage}>Previous</button>
                        </li>
                        <li className={`page-item ${!salesCanNextPage ? 'disabled' : ''}`}>
                          <button className="page-link" onClick={salesNextPage}
                            disabled={!salesCanNextPage}>Next</button>
                        </li>
                        <li><button className="page-link" onClick={() => salesGotoPage(salesPageCount - 1)}
                          disabled={!salesCanNextPage}>{'>>'}</button></li>
                      </ul>
                    </div>
                  </>
                ) : (
                  <p className="text-center text-muted py-4 fs-4 mb-0">
                    No sales recorded in this shift.
                  </p>
                )}

                {/* ── External payments — credit payoffs / refunds for past sales ── */}
                {openShift.externalPayments?.length > 0 && (
                  <div className="border-top px-4 py-3">
                    <p className="fw-semibold fs-4 mb-2">
                      Payments for Previous Sales
                      <span className="text-muted fs-5 fw-normal ms-2">
                        (credit payoffs or refunds — affect this shift's cash/bank)
                      </span>
                    </p>
                    <table className="table table-hover mb-0 fs-4">
                      <thead>
                        <tr style={{ backgroundColor: "#fff8e7" }}>
                          <th className="px-3 py-2">Time</th>
                          <th className="px-3 py-2">Customer</th>
                          <th className="px-3 py-2">Method</th>
                          <th className="px-3 py-2">Note</th>
                          <th className="px-3 py-2 text-end">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {openShift.externalPayments.map((p, i) => (
                          <tr key={i}>
                            <td className="px-3 py-2 text-muted">
                              {new Date(p.createdAt).toLocaleTimeString([], {
                                hour: '2-digit', minute: '2-digit'
                              })}
                            </td>
                            <td className="px-3 py-2">
                              {p.customer?.name || p.sale?.customerName || dash()}
                            </td>
                            <td className="px-3 py-2 text-capitalize">{p.paymentMethod}</td>
                            <td className="px-3 py-2">{dash(p.note)}</td>
                            <td className={`px-3 py-2 text-end fw-semibold ${
                              p.amount < 0 ? "text-danger" : "text-success"
                            }`}>
                              {p.amount < 0 ? "" : "+"}{fmt(p.amount)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

              </div>
            )}
          </div>

          {/* ── Count Drawer ─────────────────────────────────── */}
          <div className="card border-0 shadow-sm rounded-4">
            <div className="card-header bg-white border-bottom rounded-top-4 px-4 py-3">
              <h5 className="fw-semibold mb-0 fs-3">
                Count Drawer — Enter Actual Amounts
              </h5>
            </div>
            <div className="card-body px-4 py-4">
              <div className="row g-4">

                <div className="col-12 col-md-6">
                  <label className="fs-4 fw-medium mb-2 d-block">
                    Counted Cash <span className="text-danger">*</span>
                  </label>
                  <input
                    className="form-control form-control-lg fs-3"
                    type="number" min="0"
                    placeholder={`Expected: ${fmt(openShift.expectedCash)}`}
                    value={countedCash}
                    onChange={(e) => setCountedCash(e.target.value)}
                  />
                  {cashVariance !== null && (
                    <p className={`mt-2 mb-0 fs-4 fw-semibold ${
                      cashVariance === 0 ? "text-success"
                      : cashVariance > 0 ? "text-primary" : "text-danger"
                    }`}>
                      {cashVariance === 0 ? "✓ Matches exactly"
                        : cashVariance > 0
                          ? `+${fmt(cashVariance)} birr over`
                          : `${fmt(cashVariance)} birr short`}
                    </p>
                  )}
                </div>

                <div className="col-12 col-md-6">
                  <label className="fs-4 fw-medium mb-2 d-block">
                    Counted Bank <span className="text-danger">*</span>
                  </label>
                  <input
                    className="form-control form-control-lg fs-3"
                    type="number" min="0"
                    placeholder={`Expected: ${fmt(openShift.expectedBank)}`}
                    value={countedBank}
                    onChange={(e) => setCountedBank(e.target.value)}
                  />
                  {bankVariance !== null && (
                    <p className={`mt-2 mb-0 fs-4 fw-semibold ${
                      bankVariance === 0 ? "text-success"
                      : bankVariance > 0 ? "text-primary" : "text-danger"
                    }`}>
                      {bankVariance === 0 ? "✓ Matches exactly"
                        : bankVariance > 0
                          ? `+${fmt(bankVariance)} birr over`
                          : `${fmt(bankVariance)} birr short`}
                    </p>
                  )}
                </div>

                <div className="col-12">
                  <label className="fs-4 fw-medium mb-2 d-block">
                    Note {hasVariance && <span className="text-danger">*</span>}
                  </label>
                  <textarea
                    className="form-control fs-4" rows={2}
                    placeholder={hasVariance
                      ? "Explain the variance..."
                      : "Optional note..."}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                  />
                </div>

                <div className="col-12 d-flex justify-content-center">
                  <button
                    className="btn btn-success btn-lg rounded-3 px-5 fs-3"
                    onClick={openConfirmModal}
                    disabled={countedCash === "" || countedBank === ""}>
                    🔒 Close Shift
                  </button>
                </div>

              </div>
            </div>
          </div>

          {openShift.salesCount > 0 && (
            <p className="text-center text-muted fs-5 mt-3">
              {openShift.salesCount} sales in this shift
            </p>
          )}
        </>
      )}
    </div>
  );
};